import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    List,
    ListItemButton,
    ListItemText,
    TextField,
    Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTrackUsage } from '../../hooks/useTrackUsage';
import TaskTrackingService, { taskErrorMessage } from '../../services/TaskTrackingService';
import { withAccessConfirmation } from './confirmAccess';
import { showInFiles } from './openContent';
import { fileWord, linkResultLine, missingLanguagesLine } from './taskUi';

const SEARCH_DELAY_MS = 300;

/**
 * Link: joins a plan's waiting languages to a file in Files. The file has to be made with
 * Track this work off, or its open tasks stop the link. After a link that stopped part way the
 * rest can only go to the same file. onLinked gets the result once at least one language moved.
 * file: one just made from this task, as { contentId, title }. It links on opening, and the
 * dialog stays open to try it again if that didn't finish.
 */
const LinkFileDialog = ({ open, task, plan, file, onClose, onLinked }) => {
    useTrackUsage('LinkFileDialog', 'dialog', 'tasks', open);
    const navigate = useNavigate();
    const contentType = task?.contentType;
    const word = fileWord(contentType);
    const [query, setQuery] = useState('');
    const [matches, setMatches] = useState(null);
    const [picked, setPicked] = useState(null);
    const [error, setError] = useState(null);
    const [resultLine, setResultLine] = useState(null);
    const [busy, setBusy] = useState(false);
    const lockedTo = plan?.linkedContentId
        ? { contentId: plan.linkedContentId, title: plan.linkedTitle, languages: null }
        : file || null;

    useEffect(() => {
        if (!open) return;
        setQuery('');
        setMatches(null);
        setPicked(null);
        setError(null);
        setResultLine(null);
    }, [open]);

    useEffect(() => {
        if (!open || lockedTo || !query.trim()) {
            setMatches(null);
            return undefined;
        }
        let alive = true;
        const timer = setTimeout(() => {
            TaskTrackingService.linkSearch(contentType, query.trim())
                .then((found) => alive && setMatches(found))
                .catch((e) => alive && setError(taskErrorMessage(e)));
        }, SEARCH_DELAY_MS);
        return () => {
            alive = false;
            clearTimeout(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, query, contentType, plan?.linkedContentId]);

    const target = lockedTo || picked;

    const submit = async () => {
        setBusy(true);
        setError(null);
        setResultLine(null);
        try {
            const result = await withAccessConfirmation((grant) =>
                TaskTrackingService.linkPlan(plan.planId, { contentId: target.contentId, grantContentAccess: grant }),
            );
            if (!result) return;
            if (result.failed.length > 0) {
                // Stay open so the PM reads which languages didn't go
                setResultLine(linkResultLine(result, contentType));
                onLinked(result, { keepOpen: true });
                return;
            }
            onLinked(result, { message: `${linkResultLine(result, contentType)} ${result.title}` });
        } catch (e) {
            setError(taskErrorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        if (open && file) submit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, file?.contentId]);

    const missing = picked ? missingLanguagesLine(plan?.waitingLanguages, picked.languages, contentType) : '';

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Link {word}</DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                        {error}
                    </Alert>
                )}
                {resultLine && (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                        {resultLine}
                    </Alert>
                )}
                {lockedTo ? (
                    <DialogContentText variant="body2">
                        {plan?.linkedContentId
                            ? `The other languages are already on ${lockedTo.title}. A plan links to one ${word}, so the rest go there too.`
                            : `${lockedTo.title} is in Files. All languages waiting on this task go to it.`}
                    </DialogContentText>
                ) : (
                    <>
                        <DialogContentText variant="body2">
                            All languages waiting on this task go to the {word} you pick. Its name replaces the task's title.
                        </DialogContentText>
                        <TextField
                            autoFocus
                            fullWidth
                            margin="dense"
                            label={`Find the ${word} by name`}
                            value={query}
                            onChange={(e) => {
                                setQuery(e.target.value);
                                setPicked(null);
                            }}
                        />
                        {matches?.length > 0 && (
                            <List dense aria-label="Matching files">
                                {matches.map((m) => (
                                    <ListItemButton
                                        key={m.contentId}
                                        selected={picked?.contentId === m.contentId}
                                        onClick={() => setPicked(m)}
                                    >
                                        <ListItemText
                                            primary={m.title}
                                            secondary={[m.folder, (m.languages || []).join(', ')].filter(Boolean).join(' · ')}
                                        />
                                    </ListItemButton>
                                ))}
                            </List>
                        )}
                        {matches?.length === 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Nothing by that name yet.{' '}
                                {contentType === 'VIDEO' ? (
                                    <Button size="small" onClick={() => showInFiles({ folderPath: '/', navigate })}>
                                        Go to Files
                                    </Button>
                                ) : (
                                    `Cancel and use Create ${word}.`
                                )}
                            </Typography>
                        )}
                        {missing && (
                            <Alert severity="info" sx={{ mt: 1 }}>
                                {missing}
                            </Alert>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{resultLine ? 'Close' : 'Cancel'}</Button>
                <Button variant="contained" disabled={!target || busy || (!!resultLine && !file)} onClick={submit}>
                    Link
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default LinkFileDialog;
